import React, { useState, useRef } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";
import TauriImage from "../../../TauriImage/TauriImage";
import "./TauriCalendar.css";

const TauriCalendar = ({ date, items, holidays, currentDate, handleAdd, toggle, handleClick }) => {
    const longPressTimeout = useRef(null);
    const [selectedDate, setSelectedDate] = useState(null);

    const weekDays = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

    const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

    const daysInMonth = getDaysInMonth(currentDate.getFullYear(), currentDate.getMonth());
    const firstDayIndex = getFirstDayOfMonth(currentDate.getFullYear(), currentDate.getMonth());
    const getItemDetails = (day) => {
        if (!Array.isArray(items)) return {};

        const targetDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);

        return items.find((item) => {
            const parsedItemDate = new Date(item.date); // Auto-detects both formats
            return (
                parsedItemDate.getFullYear() === targetDate.getFullYear() &&
                parsedItemDate.getMonth() === targetDate.getMonth() &&
                parsedItemDate.getDate() === targetDate.getDate()
            );
        }) || {};
    };

    const getHolidayDetails = (day) => {
        if (!Array.isArray(holidays)) return { isHoliday: false };

        const targetDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);

        const holiday = holidays.find((h) => {
            const holidayDate = new Date(h.date);
            return (
                holidayDate.getFullYear() === targetDate.getFullYear() &&
                holidayDate.getMonth() === targetDate.getMonth() &&
                holidayDate.getDate() === targetDate.getDate()
            );
        });

        return holiday ? { isHoliday: true, name: holiday.name } : { isHoliday: false };
    };

    const renderTooltip = (props, text) => <Tooltip id="tooltip" {...props}>{text}</Tooltip>;

    const handleLongPressStart = (day) => {
        longPressTimeout.current = setTimeout(() => {
            alert(`Long Press on ${day}`);
        }, 500);
    };

    const handleLongPressEnd = () => {
        clearTimeout(longPressTimeout.current);
    };

    return (
        <div className="calendar-container">
            <div className="calendar-table-wrapper">
                <table className="table">
                    <thead>
                        <tr>
                            {weekDays.map((day) => <th key={day}>{day.toUpperCase()}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {Array.from({ length: Math.ceil((firstDayIndex + daysInMonth) / 7) }, (_, rowIndex) => (
                            <tr key={rowIndex}>
                                {weekDays.map((_, colIndex) => {
                                    const dayNumber = rowIndex * 7 + colIndex - firstDayIndex + 1;
                                    if (dayNumber < 1 || dayNumber > daysInMonth) return <td key={colIndex}></td>;

                                    const holidayDetails = getHolidayDetails(dayNumber);
                                    const props = getItemDetails(dayNumber);
                                    const isHoliday = holidayDetails.isHoliday;
                                    const holidayName = holidayDetails?.name || "";


                                    const today = new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Shanghai" }));
                                    const isToday =
                                        today.getFullYear() === currentDate.getFullYear() &&
                                        today.getMonth() === currentDate.getMonth() &&
                                        today.getDate() === dayNumber;

                                    const fullYear = new Date(date.getFullYear(), date.getMonth(), dayNumber).toLocaleDateString("en-CA");
                                    const isActive = selectedDate === fullYear;

                                    return (
                                        <td
                                            key={colIndex}
                                            onClick={() => {
                                                setSelectedDate(fullYear);
                                                handleClick(fullYear);
                                            }}
                                            onMouseDown={() => handleLongPressStart(fullYear)}
                                            onMouseUp={handleLongPressEnd}
                                            onTouchStart={() => handleLongPressStart(fullYear)}
                                            onTouchEnd={handleLongPressEnd}
                                            className={`${isToday ? "current-date" : ""} ${isActive ? "active-cell" : ""}`} // Highlight selected cell
                                        >
                                            <div className="calendar-cell">
                                                <div className="display-top">
                                                    {isHoliday ? (
                                                        <OverlayTrigger placement="top" overlay={renderTooltip(props, holidayName)}>
                                                            <span className="holiday-day">{dayNumber}</span>
                                                        </OverlayTrigger>
                                                    ) : (
                                                        <span className="display-number">{dayNumber}</span>
                                                    )}
                                                    <div className="priority-icon">
                                                        {props.item?.filter(i => i.icon.priority).reverse().map((i, index) => (
                                                            <OverlayTrigger key={index} placement="top" overlay={renderTooltip(props, i.value)}>
                                                                <div className="ms-1 d-flex">
                                                                    <TauriImage
                                                                        localImage={i.icon.name}
                                                                        alt="priority-icon"
                                                                        className="calendar-icon"
                                                                        color={i.icon.color}
                                                                    />
                                                                    <div className="priority-value">{i.value}</div>
                                                                </div>
                                                            </OverlayTrigger>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className="display-bottom">
                                                    {toggle &&
                                                        (props.item?.filter(i => !i.icon.priority)?.reverse().map((i, index) => (
                                                            <OverlayTrigger key={index} placement="top" overlay={renderTooltip(props, i.value)}>
                                                                <div className="ms-1 d-flex align-items-end">
                                                                    <TauriImage
                                                                        localImage={i.icon.name}
                                                                        alt="icon"
                                                                        className="calendar-icon"
                                                                        color={i.icon.color} />
                                                                    <div className="non-priority-value">
                                                                        {i.value}
                                                                    </div>
                                                                </div>
                                                            </OverlayTrigger>
                                                        )))
                                                    }
                                                    {
                                                        isActive &&
                                                        <TauriImage
                                                            localImage="/Images/flat-color-icons--plus.svg"  // Replace with your actual image source
                                                            alt="icon"
                                                            className="add-icon"
                                                            onClick={() => handleAdd(fullYear)}
                                                        />
                                                    }
                                                </div>
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default TauriCalendar;
